/**
 * Authentication Store - Zustand Implementation
 *
 * Replaces AuthContext with Zustand for better performance and persistence.
 *
 * Benefits:
 * - No Context re-renders (components only subscribe to what they need)
 * - Token persistence via middleware (survives page reloads)
 * - Better debugging with DevTools
 * - Simpler testing (no Context Provider needed)
 * - Cross-component state access without prop-drilling
 *
 * Migration Note: This store is used internally by AuthContext wrapper
 * for backward compatibility. New code should use useAuthStore directly.
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
} from "../utils/pkce";
import logger from "../utils/logger";
import config from "../config";

// Auth0 Configuration from environment variables
const AUTH_CONFIG = {
  authorizeUrl:
    process.env.REACT_APP_AUTH0_AUTHORIZE_URL ||
    "https://services.yonomi.cloud/authorize",
  tokenUrl:
    process.env.REACT_APP_AUTH0_TOKEN_URL ||
    "https://services.yonomi.cloud/oauth/token",
  logoutUrl:
    process.env.REACT_APP_AUTH0_LOGOUT_URL ||
    "https://realtime.romancontrols.com/login",
  clientId:
    process.env.REACT_APP_AUTH0_CLIENT_ID || "wPsXH7rXtchDJYtR0ImuqgoW40E2XXIh",
  redirectUri:
    process.env.REACT_APP_AUTH0_CALLBACK_URL ||
    `${window.location.origin}/callback`,
  audience:
    process.env.REACT_APP_AUTH0_AUDIENCE || "https://services.yonomi.cloud",
  organization:
    process.env.REACT_APP_AUTH0_ORGANIZATION || "org_ziIImzfjXAU3Yev6",
  connection:
    process.env.REACT_APP_AUTH0_CONNECTION || "Romanworks-Federation-Config",
  scope:
    process.env.REACT_APP_AUTH0_SCOPE ||
    "openid email profile read:orgs write:orgs read:userOrgAssoc write:userOrgAssoc read:installs write:installs read:clientAuthZ write:clientAuthZ read:integrationAuthz write:integrationAuthz read:publicIntegrations query:installs mutate:installs offline_access",
};

// LocalStorage keys for non-persisted auth state
const TEMP_STORAGE_KEYS = {
  CODE_VERIFIER: "auth_code_verifier",
  AUTH_STATE: "auth_state",
  EXPLICIT_LOGOUT: "explicitLogout",
  LAST_AUTHENTICATED_USER: "lastAuthenticatedUserId",
};

const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ========== STATE ==========
        isAuthenticated: false,
        isLoading: true,
        isLoadingProfile: false,
        user: null,
        error: null,
        accessToken: null,
        refreshToken: null,
        idToken: null,
        expiresAt: null,

        // ========== UTILITY FUNCTIONS ==========

        /**
         * Decode JWT token to extract user information
         */
        decodeToken: (token) => {
          try {
            if (!token || typeof token !== "string") {
              return null;
            }
            const base64Url = token.split(".")[1];
            if (!base64Url) {
              return null;
            }
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split("")
                .map(
                  (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
                )
                .join("")
            );
            return JSON.parse(jsonPayload);
          } catch (err) {
            logger.error("Failed to decode token:", err);
            return null;
          }
        },

        /**
         * Check if token is expired
         */
        isTokenExpired: () => {
          const { expiresAt } = get();
          if (!expiresAt) return true;
          return Date.now() >= expiresAt;
        },

        /**
         * Check if token needs refresh (less than 5 minutes remaining)
         */
        needsTokenRefresh: () => {
          const { expiresAt } = get();
          if (!expiresAt) return true;
          const timeUntilExpiry = expiresAt - Date.now();
          const fiveMinutesInMs = 5 * 60 * 1000;
          return timeUntilExpiry <= 0 || timeUntilExpiry < fiveMinutesInMs;
        },

        // ========== USER PROFILE OPERATIONS ==========

        /**
         * Fetch user profile from backend API using federated user ID
         */
        fetchUserProfile: async (accessToken) => {
          set({ isLoadingProfile: true });

          try {
            const decoded = get().decodeToken(accessToken);
            if (!decoded || !decoded.sub) {
              logger.warn("No sub claim in token, using minimal user info");
              set({
                user: {
                  email: decoded?.email || "unknown@example.com",
                  federatedUserId: decoded?.sub || "unknown",
                  roles: [],
                },
                isLoadingProfile: false,
              });
              return;
            }

            const federatedUserId = decoded.sub;
            logger.log("Fetching profile for federated user:", federatedUserId);

            const response = await fetch(
              `${
                config.servicesApiEndpoint
              }/user/profile?federatedUserId=${encodeURIComponent(
                federatedUserId
              )}`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  "x-api-key": config.apiKey,
                  "Content-Type": "application/json",
                },
              }
            );

            if (!response.ok) {
              logger.error(
                "Failed to fetch user profile:",
                response.status,
                response.statusText
              );
              // Fall back to minimal user info from token
              set({
                user: {
                  email: decoded.email || "unknown@example.com",
                  federatedUserId: federatedUserId,
                  roles: [],
                },
                isLoadingProfile: false,
              });
              return;
            }

            const profile = await response.json();
            logger.log("User profile fetched successfully:", {
              email: profile.email,
              rolesCount: profile.roles?.length || 0,
            });

            set({
              user: {
                email: profile.email,
                federatedUserId: federatedUserId,
                firstName: profile.firstName || "",
                lastName: profile.lastName || "",
                phoneNumber: profile.phoneNumber || "",
                roles: profile.roles || [],
                userId: profile.userId, // romanworks-yp-dev1 user ID
              },
              isLoadingProfile: false,
            });
          } catch (error) {
            logger.error("Error fetching user profile:", error);
            // Fall back to minimal user info from token
            const decoded = get().decodeToken(get().accessToken);
            set({
              user: {
                email: decoded?.email || "unknown@example.com",
                federatedUserId: decoded?.sub || "unknown",
                roles: [],
              },
              isLoadingProfile: false,
            });
          }
        },

        // ========== TOKEN MANAGEMENT ==========

        /**
         * Store authentication tokens and user info
         * Handles user switching detection and cache clearing
         */
        storeAuthData: async (tokens) => {
          logger.log("[authStore] ===== storeAuthData called =====");

          // SECURITY FIX: Detect if a different user is logging in
          const decoded = get().decodeToken(tokens.id_token);
          const newFederatedUserId = decoded?.sub;
          const previousFederatedUserId = localStorage.getItem(
            TEMP_STORAGE_KEYS.LAST_AUTHENTICATED_USER
          );

          logger.log(
            `[authStore] Previous user ID: ${previousFederatedUserId || "NONE"}`
          );
          logger.log(
            `[authStore] New user ID: ${newFederatedUserId || "NONE"}`
          );

          // If a different user is logging in, clear all cached data
          if (
            previousFederatedUserId &&
            newFederatedUserId &&
            previousFederatedUserId !== newFederatedUserId
          ) {
            logger.log(`[authStore] ⚠️  DIFFERENT USER DETECTED!`);
            logger.log(`[authStore] Previous: ${previousFederatedUserId}`);
            logger.log(`[authStore] New: ${newFederatedUserId}`);
            logger.log(
              "[authStore] 🧹 Clearing all cached data to prevent data leakage"
            );

            // Clear sessionStorage (device states, sync timestamps)
            sessionStorage.clear();
            logger.log("[authStore] ✅ sessionStorage cleared");

            // Clear localStorage cache items
            localStorage.removeItem("orgs_cache");
            localStorage.removeItem("orgs_cache_timestamp");
            logger.log("[authStore] ✅ localStorage org cache cleared");
          } else if (!previousFederatedUserId) {
            logger.log("[authStore] First login detected (no previous user)");
          } else if (previousFederatedUserId === newFederatedUserId) {
            logger.log("[authStore] Same user logging in again");
          }

          // Store the new user ID
          if (newFederatedUserId) {
            localStorage.setItem(
              TEMP_STORAGE_KEYS.LAST_AUTHENTICATED_USER,
              newFederatedUserId
            );
            logger.log(`[authStore] Stored new user ID: ${newFederatedUserId}`);
          }

          // Calculate expiration time
          const expiresAt = Date.now() + tokens.expires_in * 1000;

          logger.log(
            "[authStore] About to dispatch tokenRefreshed event, expiresAt:",
            expiresAt,
            "new Date(expiresAt):",
            new Date(expiresAt)
          );

          // Update store state (persisted via middleware)
          set({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token || get().refreshToken,
            idToken: tokens.id_token,
            expiresAt,
            isAuthenticated: true,
            error: null,
          });

          // Dispatch custom event to notify components of token refresh
          setTimeout(() => {
            logger.log("[authStore] Dispatching tokenRefreshed event NOW");
            window.dispatchEvent(
              new CustomEvent("tokenRefreshed", { detail: { expiresAt } })
            );
          }, 0);

          // Fetch user profile from backend API
          await get().fetchUserProfile(tokens.access_token);
        },

        /**
         * Clear all authentication data
         */
        clearAuthData: () => {
          logger.log("[authStore] ===== clearAuthData called =====");
          logger.log("[authStore] Clearing auth state");

          // Clear temporary storage items (not persisted by Zustand)
          localStorage.removeItem(TEMP_STORAGE_KEYS.CODE_VERIFIER);
          // NOTE: Don't clear AUTH_STATE here - needed for OAuth callback validation

          // SECURITY FIX: Clear all sessionStorage
          logger.log("[authStore] 🧹 Clearing sessionStorage");
          sessionStorage.clear();
          logger.log("[authStore] ✅ sessionStorage cleared");

          // Reset store state (Zustand persist will update localStorage)
          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            idToken: null,
            expiresAt: null,
            error: null,
          });

          logger.log("[authStore] ===== clearAuthData complete =====");
        },

        /**
         * Refresh access token using refresh token
         */
        refreshAccessToken: async () => {
          const { refreshToken, storeAuthData, clearAuthData } = get();

          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          try {
            const response = await fetch(AUTH_CONFIG.tokenUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({
                grant_type: "refresh_token",
                refresh_token: refreshToken,
                client_id: AUTH_CONFIG.clientId,
              }),
            });

            if (!response.ok) {
              const errorBody = await response.text();
              logger.warn("[authStore] Token refresh failed:", {
                status: response.status,
                statusText: response.statusText,
                body: errorBody,
              });
              throw new Error(
                `Failed to refresh token: ${response.status} ${response.statusText}`
              );
            }

            const tokens = await response.json();
            logger.log(
              "[authStore] Token refreshed successfully. New expires_in:",
              tokens.expires_in,
              "seconds"
            );
            await storeAuthData(tokens);
            return tokens.access_token;
          } catch (err) {
            logger.warn("Token refresh failed:", err);
            clearAuthData();
            throw err;
          }
        },

        /**
         * Get valid access token (refreshes if needed)
         * Proactively refreshes when token has less than 5 minutes remaining
         */
        getAccessToken: async () => {
          const { needsTokenRefresh, refreshAccessToken, accessToken } = get();

          if (needsTokenRefresh()) {
            logger.log(
              "[authStore] Token expiring soon or expired, refreshing..."
            );
            return await refreshAccessToken();
          }

          return accessToken;
        },

        // ========== AUTHENTICATION FLOW ==========

        /**
         * Initiate login flow with PKCE
         */
        login: async () => {
          try {
            set({ error: null });

            // Generate PKCE parameters
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            const state = generateState();

            // Store for later verification (not persisted)
            localStorage.setItem(TEMP_STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
            localStorage.setItem(TEMP_STORAGE_KEYS.AUTH_STATE, state);

            // Build authorization URL
            const params = new URLSearchParams({
              response_type: "code",
              client_id: AUTH_CONFIG.clientId,
              redirect_uri: AUTH_CONFIG.redirectUri,
              scope: AUTH_CONFIG.scope,
              state: state,
              code_challenge: codeChallenge,
              code_challenge_method: "S256",
              audience: AUTH_CONFIG.audience,
              organization: AUTH_CONFIG.organization,
            });

            // Only add connection if it's defined
            if (AUTH_CONFIG.connection) {
              params.append("connection", AUTH_CONFIG.connection);
            }

            // If user explicitly logged out, force login prompt
            if (
              localStorage.getItem(TEMP_STORAGE_KEYS.EXPLICIT_LOGOUT) === "true"
            ) {
              params.append("prompt", "login");
              localStorage.removeItem(TEMP_STORAGE_KEYS.EXPLICIT_LOGOUT);
            }

            const authUrl = `${AUTH_CONFIG.authorizeUrl}?${params.toString()}`;

            // Redirect to Auth0
            window.location.href = authUrl;
          } catch (err) {
            logger.error("Login initiation failed:", err);
            set({ error: "Failed to initiate login. Please try again." });
          }
        },

        /**
         * Handle OAuth callback after authorization
         */
        handleCallback: async (code, state) => {
          try {
            // Verify state parameter to prevent CSRF attacks
            const storedState = localStorage.getItem(
              TEMP_STORAGE_KEYS.AUTH_STATE
            );

            logger.log("[authStore] Callback - received state:", state);
            logger.log("[authStore] Callback - stored state:", storedState);

            if (state !== storedState) {
              logger.error(
                "[authStore] State mismatch! Received:",
                state,
                "Stored:",
                storedState
              );
              throw new Error("State mismatch - possible CSRF attack");
            }

            // Get the stored code verifier
            const codeVerifier = localStorage.getItem(
              TEMP_STORAGE_KEYS.CODE_VERIFIER
            );

            if (!codeVerifier) {
              throw new Error("Code verifier not found");
            }

            // Exchange authorization code for tokens
            const tokenResponse = await fetch(AUTH_CONFIG.tokenUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                grant_type: "authorization_code",
                client_id: AUTH_CONFIG.clientId,
                code,
                code_verifier: codeVerifier,
                redirect_uri: AUTH_CONFIG.redirectUri,
              }),
            });

            if (!tokenResponse.ok) {
              const errorText = await tokenResponse.text();
              let errorMessage;
              try {
                const errorData = JSON.parse(errorText);
                errorMessage =
                  errorData.error_description || "Token exchange failed";
              } catch (e) {
                errorMessage = `Token exchange failed: ${tokenResponse.status} ${tokenResponse.statusText}`;
                logger.error("Response was not JSON:", errorText);
              }
              throw new Error(errorMessage);
            }

            const tokens = await tokenResponse.json();

            // Store tokens and user info
            await get().storeAuthData(tokens);

            // Clean up PKCE parameters after successful authentication
            localStorage.removeItem(TEMP_STORAGE_KEYS.CODE_VERIFIER);
            localStorage.removeItem(TEMP_STORAGE_KEYS.AUTH_STATE);

            logger.log(
              "[authStore] Callback successful, tokens stored and PKCE parameters cleaned up"
            );

            return true;
          } catch (error) {
            logger.error("Callback handling failed:", error);
            set({ error: error.message });

            // Clean up PKCE parameters on error
            localStorage.removeItem(TEMP_STORAGE_KEYS.CODE_VERIFIER);
            localStorage.removeItem(TEMP_STORAGE_KEYS.AUTH_STATE);

            return false;
          }
        },

        /**
         * Logout user and redirect to Auth0 logout
         */
        logout: (reason = null) => {
          logger.log("[authStore] ===== logout called =====");
          logger.log(`[authStore] Logout reason: ${reason || "none"}`);

          get().clearAuthData();

          // DO NOT remove lastAuthenticatedUserId - needed to detect user switches
          logger.log(
            "[authStore] Keeping lastAuthenticatedUserId for user switch detection"
          );

          // Mark that user explicitly logged out
          localStorage.setItem(TEMP_STORAGE_KEYS.EXPLICIT_LOGOUT, "true");

          // Store logout reason if provided (after sessionStorage.clear)
          if (reason) {
            sessionStorage.setItem("logoutReason", reason);
          }

          logger.log("[authStore] Redirecting to logout URL...");

          // Redirect to configured logout URL directly (params are embedded in the URL)
          window.location.href = AUTH_CONFIG.logoutUrl;
        },

        // ========== INITIALIZATION ==========

        /**
         * Check authentication status on mount
         * Called automatically by store initialization
         */
        checkAuth: async () => {
          try {
            logger.log("[authStore] Checking authentication status...");
            const {
              accessToken,
              idToken,
              isTokenExpired,
              refreshAccessToken,
              refreshToken,
              clearAuthData,
              fetchUserProfile,
            } = get();

            logger.log("[authStore] Access token exists:", !!accessToken);
            logger.log("[authStore] ID token exists:", !!idToken);

            if (!accessToken || !idToken) {
              logger.log("[authStore] No tokens found, user not authenticated");
              set({ isAuthenticated: false, isLoading: false });
              return;
            }

            // Check if token is expired
            const expired = isTokenExpired();
            logger.log("[authStore] Token expired:", expired);

            if (expired) {
              // Try to refresh
              logger.log("[authStore] Refresh token exists:", !!refreshToken);

              if (refreshToken) {
                try {
                  logger.log("[authStore] Attempting to refresh token...");
                  await refreshAccessToken();
                  logger.log("[authStore] Token refresh successful");
                } catch (err) {
                  logger.warn("[authStore] Token refresh failed:", err);
                  clearAuthData();
                }
              } else {
                logger.log("[authStore] No refresh token, clearing auth data");
                clearAuthData();
              }
            } else {
              // Token is valid, fetch user profile from backend
              logger.log(
                "[authStore] Token valid, fetching user profile from backend"
              );
              set({ isAuthenticated: true });
              await fetchUserProfile(accessToken);
            }
          } catch (err) {
            logger.error("[authStore] Auth check failed:", err);
            get().clearAuthData();
          } finally {
            set({ isLoading: false });
          }
        },

        // ========== UTILITY GETTERS ==========

        /**
         * Get token expiration timestamp
         */
        getTokenExpiresAt: () => {
          return get().expiresAt;
        },
      }),
      {
        name: "auth-storage", // localStorage key
        // Only persist essential auth data
        partialize: (state) => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          idToken: state.idToken,
          expiresAt: state.expiresAt,
          isAuthenticated: state.isAuthenticated,
          user: state.user,
        }),
      }
    ),
    { name: "AuthStore" } // DevTools name
  )
);

// Initialize auth check when store is created
// This runs once when the module is first imported
setTimeout(() => {
  useAuthStore.getState().checkAuth();
}, 0);

// Periodic token validation check (every 2 minutes)
let tokenCheckInterval = null;

// Subscribe to authentication state to start/stop interval
useAuthStore.subscribe((state, prevState) => {
  if (state.isAuthenticated && !prevState.isAuthenticated) {
    // User just logged in, start periodic check
    logger.log(
      "[authStore] Starting periodic token validation (every 2 minutes)"
    );

    tokenCheckInterval = setInterval(async () => {
      logger.log("[authStore] Periodic check: Validating token...");

      const { isTokenExpired, refreshAccessToken, refreshToken, logout } =
        useAuthStore.getState();
      const expired = isTokenExpired();

      if (expired) {
        logger.log(
          "[authStore] Periodic check: Token expired, attempting refresh..."
        );

        if (refreshToken) {
          try {
            await refreshAccessToken();
            logger.log("[authStore] Periodic check: Token refresh successful");
          } catch (err) {
            logger.error(
              "[authStore] Periodic check: Token refresh failed, logging out",
              err
            );
            logout("session_expired");
          }
        } else {
          logger.warn(
            "[authStore] Periodic check: No refresh token available, logging out"
          );
          logout("session_expired");
        }
      } else {
        logger.log("[authStore] Periodic check: Token still valid");
      }
    }, 2 * 60 * 1000); // 2 minutes
  } else if (!state.isAuthenticated && prevState.isAuthenticated) {
    // User just logged out, stop periodic check
    if (tokenCheckInterval) {
      logger.log("[authStore] Stopping periodic token validation");
      clearInterval(tokenCheckInterval);
      tokenCheckInterval = null;
    }
  }
});

export default useAuthStore;
